import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { useProductContext } from '../contexts/ProductContext';
import { formatCurrency } from '../utils/formatters';
import { toast } from 'react-toastify';
import Modal from '../components/common/Modal';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import './Products.css';

const CATEGORIES = [
  { value: 'Comida', label: 'Comida', icon: '🍔' },
  { value: 'Bebida', label: 'Bebidas', icon: '🥤' },
  { value: 'Snack', label: 'Snacks', icon: '🍿' },
  { value: 'Postre', label: 'Postres', icon: '🍰' },
  { value: 'Otro', label: 'Otro', icon: '📦' }
];

const Products = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { getProductsByVendor, addProduct, updateProduct, deleteProduct } = useProductContext();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  const [formData, setFormData] = useState({
    nombre: '',
    precio: '',
    categoria: 'Comida',
    disponible: true
  });

  const products = getProductsByVendor(user?.id);
  const filteredProducts = filterCategory === 'all' 
    ? products 
    : products.filter(p => p.categoria === filterCategory);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      precio: '',
      categoria: 'Comida',
      disponible: true
    });
  };

  const handleAddProduct = () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    const precio = parseFloat(formData.precio);
    if (isNaN(precio) || precio <= 0) {
      toast.error('El precio debe ser mayor a cero');
      return;
    }

    const newProduct = {
      nombre: formData.nombre.trim(),
      precio: precio,
      categoria: formData.categoria,
      disponible: formData.disponible,
      vendedorId: user.id
    };

    addProduct(newProduct);
    toast.success(`${newProduct.nombre} agregado correctamente`);
    setShowAddModal(false);
    resetForm();
  };

  const handleEditClick = (product) => {
    setSelectedProduct(product);
    setFormData({
      nombre: product.nombre,
      precio: product.precio.toString(),
      categoria: product.categoria,
      disponible: product.disponible
    });
    setShowEditModal(true);
  };

  const handleUpdateProduct = () => {
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    const precio = parseFloat(formData.precio);
    if (isNaN(precio) || precio <= 0) {
      toast.error('El precio debe ser mayor a cero');
      return;
    }

    const updatedProduct = {
      ...selectedProduct,
      nombre: formData.nombre.trim(),
      precio: precio,
      categoria: formData.categoria,
      disponible: formData.disponible
    };

    updateProduct(selectedProduct.id, updatedProduct);
    toast.success(`${updatedProduct.nombre} actualizado`);
    setShowEditModal(false);
    setSelectedProduct(null);
    resetForm();
  };

  const handleDeleteClick = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    deleteProduct(selectedProduct.id);
    toast.success(`${selectedProduct.nombre} eliminado`);
    setShowDeleteModal(false);
    setSelectedProduct(null);
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : '📦';
  };

  const stats = {
    total: products.length,
    disponibles: products.filter(p => p.disponible).length,
    noDisponibles: products.filter(p => !p.disponible).length
  };

  return (
    <div className="products-container">
      {/* Header */}
      <header className="products-header">
        <div className="header-content">
          <button onClick={() => navigate('/dashboard')} className="back-btn">
            <span className="back-icon">←</span>
            <span>Volver</span>
          </button>
          <div className="header-title">
            <div className="title-icon">🛍️</div>
            <div>
              <h1>Mis Productos</h1>
              <p>Gestiona tu catálogo</p>
            </div>
          </div>
          <div className="header-actions">
            <Button
              variant="primary"
              onClick={() => setShowAddModal(true)}
              leftIcon="+"
            >
              Nuevo Producto
            </Button>
          </div>
        </div>
      </header>

      <div className="products-content">
        {/* Stats */}
        <div className="stats-grid">
          <Card variant="gradient" padding="medium" className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-info">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Productos</div>
            </div>
          </Card>

          <Card variant="default" padding="medium" className="stat-card">
            <div className="stat-icon" style={{ background: '#10b981' }}>✓</div>
            <div className="stat-info">
              <div className="stat-value">{stats.disponibles}</div>
              <div className="stat-label">Disponibles</div>
            </div>
          </Card>

          <Card variant="default" padding="medium" className="stat-card">
            <div className="stat-icon" style={{ background: '#ef4444' }}>✗</div>
            <div className="stat-info">
              <div className="stat-value">{stats.noDisponibles}</div>
              <div className="stat-label">No Disponibles</div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card variant="default" padding="medium" className="filters-card">
          <div className="filter-label">
            <span className="label-icon">📂</span>
            Categoría
          </div>
          <div className="categories-filter">
            <button
              className={`category-btn ${filterCategory === 'all' ? 'active' : ''}`}
              onClick={() => setFilterCategory('all')}
            >
              <span className="cat-icon">📋</span>
              Todas ({products.length})
            </button>
            {CATEGORIES.map(cat => {
              const count = products.filter(p => p.categoria === cat.value).length;
              return (
                <button
                  key={cat.value}
                  className={`category-btn ${filterCategory === cat.value ? 'active' : ''}`}
                  onClick={() => setFilterCategory(cat.value)}
                >
                  <span className="cat-icon">{cat.icon}</span>
                  {cat.label} ({count})
                </button>
              );
            })}
          </div>
        </Card>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <Card variant="default" padding="large" className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No hay productos</h3>
            <p>
              {filterCategory === 'all'
                ? 'Comienza agregando tu primer producto'
                : `No hay productos en la categoría ${CATEGORIES.find(c => c.value === filterCategory)?.label}`}
            </p>
            {filterCategory === 'all' && (
              <Button
                variant="primary"
                onClick={() => setShowAddModal(true)}
                leftIcon="+"
              >
                Agregar Producto
              </Button>
            )}
          </Card>
        ) : (
          <div className="products-grid">
            {filteredProducts.map(product => (
              <Card
                key={product.id}
                variant="default"
                padding="medium"
                className="product-card"
              >
                <div className="product-header">
                  <div className="product-icon">
                    {getCategoryIcon(product.categoria)}
                  </div>
                  {!product.disponible && (
                    <span className="unavailable-badge">No disponible</span>
                  )}
                </div>

                <div className="product-body">
                  <h3 className="product-name">{product.nombre}</h3>
                  <div className="product-category">{product.categoria}</div>
                  <div className="product-price">{formatCurrency(product.precio)}</div>
                </div>

                <div className="product-actions">
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => handleEditClick(product)}
                    leftIcon="✏️"
                  >
                    Editar
                  </Button>
                  <Button
                    variant="danger"
                    size="small"
                    onClick={() => handleDeleteClick(product)}
                    leftIcon="🗑️"
                  >
                    Eliminar
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetForm();
        }}
        title="Agregar Producto"
        size="medium"
      >
        <div className="product-form">
          <div className="form-group">
            <label className="form-label">
              <span className="label-icon">📝</span>
              Nombre del producto
            </label>
            <input
              type="text"
              name="nombre"
              className="form-input"
              placeholder="Ej: Hamburguesa"
              value={formData.nombre}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <span className="label-icon">💵</span>
              Precio
            </label>
            <div className="price-input-wrapper">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                name="precio"
                className="form-input price-input"
                placeholder="0"
                value={formData.precio}
                onChange={handleInputChange}
                min="0"
                step="100"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <span className="label-icon">📂</span>
              Categoría
            </label>
            <select
              name="categoria"
              className="form-select"
              value={formData.categoria}
              onChange={handleInputChange}
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                name="disponible"
                checked={formData.disponible}
                onChange={handleInputChange}
              />
              <span className="checkbox-label">Producto disponible</span>
            </label>
          </div>

          <div className="form-actions">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setShowAddModal(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={handleAddProduct}
            >
              Agregar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Product Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedProduct(null);
          resetForm();
        }}
        title="Editar Producto"
        size="medium"
      >
        <div className="product-form">
          <div className="form-group">
            <label className="form-label">
              <span className="label-icon">📝</span>
              Nombre del producto
            </label>
            <input
              type="text"
              name="nombre"
              className="form-input"
              placeholder="Ej: Hamburguesa"
              value={formData.nombre}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <span className="label-icon">💵</span>
              Precio
            </label>
            <div className="price-input-wrapper">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                name="precio"
                className="form-input price-input"
                placeholder="0"
                value={formData.precio}
                onChange={handleInputChange}
                min="0"
                step="100"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <span className="label-icon">📂</span>
              Categoría
            </label>
            <select
              name="categoria"
              className="form-select"
              value={formData.categoria}
              onChange={handleInputChange}
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                name="disponible"
                checked={formData.disponible}
                onChange={handleInputChange}
              />
              <span className="checkbox-label">Producto disponible</span>
            </label>
          </div>

          <div className="form-actions">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setShowEditModal(false);
                setSelectedProduct(null);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={handleUpdateProduct}
            >
              Guardar Cambios
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedProduct(null);
        }}
        title="Confirmar Eliminación"
        size="small"
      >
        {selectedProduct && (
          <div className="delete-confirmation">
            <div className="delete-icon">⚠️</div>
            <h3>¿Eliminar producto?</h3>
            <p>
              Estás a punto de eliminar <strong>{selectedProduct.nombre}</strong>.
              Esta acción no se puede deshacer.
            </p>
            <div className="form-actions">
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedProduct(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="danger"
                fullWidth
                onClick={handleConfirmDelete}
              >
                Eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Products;
